import { useState } from 'react';
import { Stack, Group, Text, Button, Badge } from '@repletoryreach/sdk-ui';
import { TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import type { Endpoint } from '../spec';
import { pathParameters, queryParameters, isWrite, isDestructive } from '../spec';
import type { RequestValues } from '../curl';
import { buildRequest, bodyError, missingPathValues } from '../curl';
import { CodeBlock } from './CodeBlock';

interface Outcome {
  status: number;
  statusText: string;
  ms: number;
  headers: Record<string, string>;
  body: string;
}

/** A failure with no response at all: the request never arrived. */
interface Blocked {
  reason: string;
}

/**
 * Runs one operation against the developer's real account, from their browser,
 * with the key they pasted.
 *
 * <b>This is a live call.</b> There is no sandbox and no test mode: a contact
 * created here is a contact, a message sent here is a message somebody
 * receives, and a delete here is permanent. So a write is not run until it is
 * confirmed, and a delete is not run until the resource has been named by hand.
 *
 * The request goes from this page straight to the API, cross-origin. It does
 * not pass through the console's own server, and the key is not stored anywhere
 * on the way. If the gateway has not been told to accept this console's origin,
 * the browser refuses the call before it leaves and there is no response to
 * show; that case is reported as what it is, with the curl command as the way
 * round it, rather than being quietly rerouted through a server of ours.
 */
export function TryIt({
  endpoint,
  baseUrl,
  values,
  onValuesChange,
  apiKey
}: {
  endpoint: Endpoint;
  baseUrl: string;
  values: RequestValues;
  onValuesChange: (values: RequestValues) => void;
  apiKey: string;
}) {
  const t = useT();
  const [confirmed, setConfirmed] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [blocked, setBlocked] = useState<Blocked | null>(null);

  const pathParams = pathParameters(endpoint.operation);
  const queryParams = queryParameters(endpoint.operation);
  const write = isWrite(endpoint.method);
  const destructive = isDestructive(endpoint.method);

  const hasKey = apiKey.trim().length > 0;
  const missing = missingPathValues(endpoint, values);
  const badBody = bodyError(values.body);

  // A delete is only armed once the developer has typed the very resource id
  // they are about to remove, character for character. Choosing the wrong row
  // in a list is easy; retyping the wrong id is not.
  const deleteTarget = pathParams[0] ? values.path[pathParams[0].name]?.trim() ?? '' : '';
  const named = !destructive || (deleteTarget.length > 0 && typedName.trim() === deleteTarget);

  const ready = hasKey && missing.length === 0 && !badBody && (!write || confirmed) && named;

  async function run() {
    const request = buildRequest(endpoint, baseUrl, values, apiKey);
    setRunning(true);
    setOutcome(null);
    setBlocked(null);
    const started = performance.now();
    try {
      const response = await fetch(request.url, {
        method: request.method,
        headers: request.headers,
        body: request.body,
        // No cookies: this call is authenticated by the key and nothing else,
        // so it must not quietly borrow the console's own session.
        credentials: 'omit'
      });
      const elapsed = Math.round(performance.now() - started);
      const text = await response.text();
      let pretty = text;
      try {
        pretty = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        // Not JSON. Show exactly what came back rather than nothing.
      }
      const headers: Record<string, string> = {};
      response.headers.forEach((value, name) => {
        headers[name] = value;
      });
      setOutcome({
        status: response.status,
        statusText: response.statusText,
        ms: elapsed,
        headers,
        body: pretty
      });
    } catch (error) {
      // `fetch` rejects with a bare TypeError for a blocked request and tells
      // the page nothing more; the browser's reason is in the console and not
      // available to us. So say what it means rather than pretending to know.
      setBlocked({ reason: error instanceof Error ? error.message : String(error) });
    } finally {
      setRunning(false);
      setConfirmed(false);
      setTypedName('');
    }
  }

  return (
    <Stack gap="md">
      {pathParams.length > 0 && (
        <Stack gap="sm">
          <Text role="label">{t('developers.try.pathValues')}</Text>
          {pathParams.map((p) => (
            <TextInput
              key={p.name}
              label={p.name}
              value={values.path[p.name] ?? ''}
              onChange={(e) =>
                onValuesChange({ ...values, path: { ...values.path, [p.name]: e.currentTarget.value } })
              }
              description={p.description ?? t('developers.try.pathHint', { name: p.name })}
            />
          ))}
        </Stack>
      )}

      {queryParams.length > 0 && (
        <Stack gap="sm">
          <Text role="label">{t('developers.try.queryValues')}</Text>
          {queryParams.map((p) => (
            <TextInput
              key={p.name}
              label={p.name}
              value={values.query[p.name] ?? ''}
              onChange={(e) =>
                onValuesChange({ ...values, query: { ...values.query, [p.name]: e.currentTarget.value } })
              }
              description={p.description ?? t('developers.try.queryHint')}
            />
          ))}
        </Stack>
      )}

      {write && (
        <Textarea
          label={t('developers.try.body')}
          value={values.body}
          onChange={(e) => onValuesChange({ ...values, body: e.currentTarget.value })}
          rows={8}
          spellCheck={false}
          error={badBody ? t('developers.try.badJson') : undefined}
          description={t('developers.try.bodyHint')}
        />
      )}

      {!hasKey && <Alert color="info">{t('developers.try.needKey')}</Alert>}

      {write && (
        <Alert color="warning" title={t('developers.try.liveTitle')}>
          <Stack gap="sm">
            <Text role="body">{t('developers.try.liveBody')}</Text>
            {destructive ? (
              <Stack gap="sm">
                <Text role="body">{t('developers.try.deleteBody')}</Text>
                <TextInput
                  label={t('developers.try.deleteLabel')}
                  value={typedName}
                  onChange={(e) => setTypedName(e.currentTarget.value)}
                  spellCheck={false}
                  description={
                    deleteTarget
                      ? t('developers.try.deleteHint', { id: deleteTarget })
                      : t('developers.try.deleteNoTarget')
                  }
                />
              </Stack>
            ) : (
              <Button
                size="sm"
                variant={confirmed ? 'filled' : 'outline'}
                color="warning"
                onClick={() => setConfirmed((was) => !was)}
              >
                {confirmed ? t('developers.try.confirmed') : t('developers.try.confirm')}
              </Button>
            )}
          </Stack>
        </Alert>
      )}

      <Group gap="sm" wrap="wrap">
        <Button onClick={run} disabled={!ready} loading={running}>
          {t('developers.try.send')}
        </Button>
        {missing.length > 0 && (
          <Text role="caption" c="var(--rr-color-text-muted)">
            {t('developers.try.missing', { names: missing.join(', ') })}
          </Text>
        )}
      </Group>

      {blocked && (
        <Alert color="error" title={t('developers.try.blockedTitle')}>
          <Stack gap="sm">
            <Text role="body">{t('developers.try.blockedBody')}</Text>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('developers.try.blockedDetail', { reason: blocked.reason })}
            </Text>
          </Stack>
        </Alert>
      )}

      {outcome && (
        <Stack gap="sm">
          <Group gap="sm" wrap="wrap" align="center">
            <Badge color={outcome.status < 400 ? 'success' : outcome.status < 500 ? 'warning' : 'error'}>
              {outcome.status} {outcome.statusText}
            </Badge>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('developers.try.timing', { ms: String(outcome.ms) })}
            </Text>
            {outcome.headers['x-ratelimit-remaining'] && (
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t('developers.try.remaining', {
                  remaining: outcome.headers['x-ratelimit-remaining'],
                  limit: outcome.headers['x-ratelimit-limit'] ?? '?'
                })}
              </Text>
            )}
          </Group>
          <CodeBlock code={outcome.body || t('developers.try.emptyBody')} label={t('developers.try.response')} />
        </Stack>
      )}
    </Stack>
  );
}
